/* ==========================================================================
   Amyra — theme runtime
   Vanilla replacements for the Framer Motion behaviour in the Hydrogen build:
   scroll reveal, parallax, the header's transparent/solid switch, the mobile
   drawer, and a shared carousel used by the hero, categories and testimonials.
   No dependencies, no build step.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /* ------------------------------------------------------------------ *
   * Scroll reveal
   * ------------------------------------------------------------------ */

  function initReveal(root) {
    var targets = (root || document).querySelectorAll(
      '[data-reveal]:not([data-reveal-ready]), [data-reveal-stagger]:not([data-reveal-ready]), [data-reveal-rule]:not([data-reveal-ready])'
    );
    if (!targets.length) return;

    // Without IntersectionObserver, or with motion turned down, show everything.
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.setAttribute('data-reveal-ready', '');
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-reveal-ready', '');
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Parallax
   * Elements carry data-parallax="<speed>"; we write --parallax-y and let
   * CSS do the transform. One rAF-throttled scroll listener for the page.
   * ------------------------------------------------------------------ */

  function initParallax() {
    if (prefersReducedMotion || document.body.dataset.parallax === 'off') return;

    var elements = [].slice.call(document.querySelectorAll('[data-parallax]'));
    if (!elements.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var viewportHeight = window.innerHeight;

      elements.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewportHeight) return;

        var speed = parseFloat(el.dataset.parallax) || 0.2;
        // -1 when the element is just below the fold, +1 when just above it.
        var progress =
          (viewportHeight - rect.top - rect.height / 2) /
          (viewportHeight + rect.height);
        var offset = (progress - 0.5) * 2 * speed * 100;
        el.style.setProperty('--parallax-y', offset.toFixed(2) + 'px');
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * Header — transparent over a dark hero, solid once scrolled
   * ------------------------------------------------------------------ */

  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    // Only pages that open with a dark full-bleed hero may start transparent.
    var allowTransparent = header.dataset.transparent === 'true';
    var threshold = 50;

    // An announcement bar sits in normal flow above the fixed header. Ride down
    // with it at the top of the page, then pin to the viewport once it has
    // scrolled away - otherwise the header would either cover the bar or float
    // below a gap.
    var announcement = document.querySelector('.announcement-section');

    function sync() {
      var scrolled = window.scrollY;
      var solid = !allowTransparent || scrolled > threshold;

      header.classList.toggle('header--solid', solid);
      header.classList.toggle('header--transparent', !solid);

      if (announcement) {
        var barHeight = announcement.offsetHeight;
        var top = Math.max(0, barHeight - scrolled);
        header.style.setProperty('--header-offset', top + 'px');
      }
    }

    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * Drawers (mobile menu, search)
   * ------------------------------------------------------------------ */

  function initDrawers() {
    var openers = document.querySelectorAll('[data-drawer-open]');
    var lastFocused = null;

    function close(drawer) {
      if (!drawer || !drawer.hasAttribute('open')) return;
      drawer.removeAttribute('open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (lastFocused) lastFocused.focus();
    }

    function open(drawer, trigger) {
      if (!drawer) return;
      lastFocused = trigger || null;
      drawer.setAttribute('open', '');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');

      var focusable = drawer.querySelector(
        'input, button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        // Wait for the open transition so focus does not fight the animation.
        window.setTimeout(function () {
          focusable.focus();
        }, 120);
      }
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        open(document.getElementById(btn.dataset.drawerOpen), btn);
      });
    });

    document.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        close(btn.closest('[data-drawer]'));
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-drawer][open]').forEach(close);
    });
  }

  /* ------------------------------------------------------------------ *
   * Predictive search — live product suggestions in the search drawer
   * ------------------------------------------------------------------ */

  function initPredictiveSearch() {
    var form = document.querySelector('[data-predictive-search-form]:not([data-predictive-ready])');
    if (!form) return;
    form.setAttribute('data-predictive-ready', '');

    var input = form.querySelector('input[type="search"]');
    var results = document.getElementById(input.getAttribute('aria-controls'));
    if (!input || !results) return;

    var noMatchesText = results.dataset.noMatchesText || '';
    var viewAllText = results.dataset.viewAllText || '';
    var debounceTimer;

    function close() {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }

    function render(query, products) {
      // The drawer may have been closed, or the query changed, while this was in flight.
      if (input.value.trim() !== query) return;

      if (!products.length) {
        results.innerHTML = '<p class="predictive-search__empty">' + noMatchesText.replace('SEARCH_QUERY', query) + '</p>';
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        return;
      }

      var items = products.map(function (product) {
        var image = product.featured_image
          ? '<img class="predictive-search__image" src="' + product.featured_image.url + '&width=80" alt="" width="40" height="40" loading="lazy">'
          : '';
        return (
          '<a class="predictive-search__item" href="' + product.url + '">' +
          image +
          '<span class="predictive-search__title">' + product.title + '</span>' +
          '</a>'
        );
      });

      results.innerHTML =
        '<div class="predictive-search__list">' + items.join('') + '</div>' +
        '<a class="predictive-search__view-all" href="' + form.action + '?q=' + encodeURIComponent(query) + '&type=product">' +
        viewAllText.replace('SEARCH_QUERY', query) +
        '</a>';
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    input.addEventListener('input', function () {
      var query = input.value.trim();
      window.clearTimeout(debounceTimer);

      if (query.length < 2) {
        close();
        return;
      }

      debounceTimer = window.setTimeout(function () {
        fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6')
          .then(function (response) { return response.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            render(query, products);
          })
          .catch(close);
      }, 200);
    });

    document.addEventListener('click', function (event) {
      if (!form.contains(event.target) && !results.contains(event.target)) close();
    });

    var drawer = form.closest('[data-drawer]');
    if (drawer) {
      drawer.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', close);
      });
      drawer.querySelector('.drawer__overlay').addEventListener('click', close);
    }
  }

  /* ------------------------------------------------------------------ *
   * Carousel
   * One implementation behind the hero slider, the categories rail and the
   * testimonial rotator. Behaviour is declared on the element:
   *   data-carousel="fade|scroll"  data-autoplay="7000"
   * ------------------------------------------------------------------ */

  function initCarousels(root) {
    var carousels = (root || document).querySelectorAll(
      '[data-carousel]:not([data-carousel-ready])'
    );

    carousels.forEach(function (carousel) {
      carousel.setAttribute('data-carousel-ready', '');

      var mode = carousel.dataset.carousel || 'fade';
      var slides = [].slice.call(carousel.querySelectorAll('[data-slide]'));
      var dots = [].slice.call(carousel.querySelectorAll('[data-slide-to]'));
      var track = carousel.querySelector('[data-carousel-track]');
      var interval = parseInt(carousel.dataset.autoplay, 10) || 0;
      var index = 0;
      var timer = null;

      if (slides.length < 2 && !dots.length) return;

      function render() {
        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });

        if (mode === 'scroll' && track && slides[index]) {
          // Measure against the first slide, not the track: the track carries a
          // left gutter, and subtracting its own offset would scroll that gutter
          // away as soon as the rail lands on slide zero.
          track.scrollTo({
            left: slides[index].offsetLeft - slides[0].offsetLeft,
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
          });
        }

        // Restart the CSS progress bar, when the section has one.
        var progress = carousel.querySelector('[data-carousel-progress]');
        if (progress) {
          progress.style.animation = 'none';
          void progress.offsetWidth;
          progress.style.animation = '';
        }
      }

      function goTo(next) {
        index = (next + slides.length) % slides.length;
        render();
      }

      function start() {
        if (!interval || slides.length < 2 || prefersReducedMotion) return;
        stop();
        timer = window.setInterval(function () {
          goTo(index + 1);
        }, interval);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          goTo(i);
          start();
        });
      });

      carousel.querySelectorAll('[data-slide-prev]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          goTo(index - 1);
          start();
        });
      });

      carousel.querySelectorAll('[data-slide-next]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          goTo(index + 1);
          start();
        });
      });

      // Pause while the visitor is interacting or the tab is hidden.
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      render();
      start();
    });
  }

  /* ------------------------------------------------------------------ *
   * Product gallery — thumbnails swap the main image
   * ------------------------------------------------------------------ */

  function initGalleries(root) {
    var galleries = (root || document).querySelectorAll(
      '[data-gallery]:not([data-gallery-ready])'
    );

    galleries.forEach(function (gallery) {
      gallery.setAttribute('data-gallery-ready', '');

      var main = gallery.querySelector('[data-gallery-main]');
      if (!main) return;

      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.dataset.galleryThumb;
          if (!full) return;

          main.src = full;
          if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
          main.alt = thumb.dataset.alt || main.alt;

          gallery
            .querySelectorAll('[data-gallery-thumb]')
            .forEach(function (other) {
              other.setAttribute(
                'aria-current',
                other === thumb ? 'true' : 'false'
              );
            });
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection toolbar — submit filter/sort without a separate Apply button
   * ------------------------------------------------------------------ */

  function initAutoSubmit(root) {
    (root || document)
      .querySelectorAll('[data-auto-submit]:not([data-auto-submit-ready])')
      .forEach(function (el) {
        el.setAttribute('data-auto-submit-ready', '');
        el.addEventListener('change', function () {
          var form = el.form || el.closest('form');
          if (form) form.submit();
        });
      });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init(root) {
    initReveal(root);
    initCarousels(root);
    initGalleries(root);
    initAutoSubmit(root);
  }

  function boot() {
    init(document);
    initParallax();
    initHeader();
    initDrawers();
    initPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor swaps section markup in place; re-run for the new DOM.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initParallax();
    initHeader();
    initDrawers();
    initPredictiveSearch();
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });
})();
